import { NgStyle } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { listenOutsideAngular } from '@iupics-util/tools/zone-listener.util';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ZIndexUtils } from 'primeng/utils';
import shajs from 'sha.js';

export enum InfoDialogType {
  ERROR = 'error',
  INFO = 'info',
  CONFIRM = 'confirm',
  CONFIRM_YESNO = 'confirmyesno',
}

@Component({
  selector: 'iu-info-dialog',
  templateUrl: './info-dialog.component.html',
  styleUrls: ['./info-dialog.component.scss'],
  standalone: true,
  imports: [NgStyle, ToastModule, SharedModule, ButtonModule, TranslateModule],
})
export default class InfoDialogComponent implements OnInit, OnDestroy {
  @Input()
  message: {
    summary: string;
    detail: string;
  };
  @Input()
  dialogType: InfoDialogType;

  @Output()
  readonly confirm = new EventEmitter<void>();
  @Output()
  readonly cancel = new EventEmitter<void>();
  @ViewChild('dialogContainer', { static: true }) dialogContainerRef: ElementRef<HTMLInputElement>;
  key: string;

  isOpen = false;

  /** optional for one question: action verb on the buttons and a danger style (reset when it closes) */
  confirmLabel: string = null;
  cancelLabel: string = null;
  danger = false;
  /** optional third choice of a Yes/No question: close it without answering (e.g. stay on the record) */
  stayLabel: string = null;
  @Output()
  readonly dismiss = new EventEmitter<void>();
  #previousFocus: HTMLElement = null;
  readonly #keydown = listenOutsideAngular('keydown', () => this.isOpen, (event) => this.onDocumentKeydown(event));

  baseZIndex = 5000;

  constructor(
    private messageService: MessageService,
    private el: ElementRef
  ) {}

  ngOnInit() {
    if (this.dialogContainerRef) {
      ZIndexUtils.set('info-dialog', this.dialogContainerRef?.nativeElement, this.baseZIndex);
    }
    if (this.message) {
      this.key = shajs('sha256')
        .update(this.message.summary + this.message.detail + this.dialogType + Date.now())
        .digest('hex');
    } else {
      this.key = shajs('sha256')
        .update('global' + Date.now())
        .digest('hex');
    }
  }
  ngOnDestroy(): void {
    if (this.dialogContainerRef) {
      ZIndexUtils.clear(this.dialogContainerRef?.nativeElement);
    }
  }

  confirmDialog() {
    this.isOpen = false;
    this.#afterClose();
    this.confirm.emit();
    this.messageService.clear(this.key);
    if (this.dialogContainerRef) {
      ZIndexUtils.clear(this.dialogContainerRef?.nativeElement);
    }
  }

  cancelDialog() {
    this.isOpen = false;
    this.#afterClose();
    this.cancel.emit();
    this.messageService.clear(this.key);
    if (this.dialogContainerRef) {
      ZIndexUtils.clear(this.dialogContainerRef?.nativeElement);
    }
  }

  dismissDialog() {
    this.isOpen = false;
    this.#afterClose();
    this.dismiss.emit();
    this.messageService.clear(this.key);
    if (this.dialogContainerRef) {
      ZIndexUtils.clear(this.dialogContainerRef?.nativeElement);
    }
  }

  /** a click beside the dialog never answers a Yes/No question (its "No" may discard changes) */
  onBackdropClick() {
    if (!this.#isConfirmType()) {
      this.confirmDialog();
    } else if (this.stayLabel) {
      this.dismissDialog();
    } else if (this.dialogType === InfoDialogType.CONFIRM || this.danger) {
      this.cancelDialog();
    }
  }

  showInfoDialog() {
    if (this.dialogContainerRef) {
      ZIndexUtils.set('info-dialog', this.dialogContainerRef?.nativeElement, this.baseZIndex);
    }
    this.isOpen = true;
    this.#previousFocus = document.activeElement as HTMLElement;
    // focus the dialog: the safe choice (cancel) for a dangerous action, else the main button
    setTimeout(() => {
      if (!this.isOpen) return;
      const selector =
        this.danger && this.#isConfirmType() ? '[data-cy="info-dialog-cancel-btn"]' : '.info-dialog-buttons .p-button-primary';
      (this.el.nativeElement as HTMLElement).querySelector<HTMLElement>(selector)?.focus();
    });
    this.messageService.add({
      key: this.key,
      sticky: true,
      closable: false,
      severity:
        this.dialogType === InfoDialogType.CONFIRM || this.dialogType === InfoDialogType.CONFIRM_YESNO
          ? 'warn'
          : this.dialogType,
      summary: this.message.summary,
      detail: this.message.detail,
    });
  }

  /**
   * Escape closes an information, cancels an OK/Cancel question and a dangerous Yes/No one. A plain Yes/No
   * question may have a real "No" (e.g. discard the changes): Escape does not answer it.
   * Tab stays on the dialog buttons.
   */
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!this.#isConfirmType()) {
        this.confirmDialog();
      } else if (this.stayLabel) {
        this.dismissDialog();
      } else if (this.dialogType === InfoDialogType.CONFIRM || this.danger) {
        this.cancelDialog();
      }
    } else if (event.key === 'Tab') {
      const buttons = Array.from(
        (this.el.nativeElement as HTMLElement).querySelectorAll<HTMLElement>('.info-dialog-buttons button')
      );
      if (!buttons.length) return;
      const index = buttons.indexOf(document.activeElement as HTMLElement);
      const next = event.shiftKey
        ? index <= 0
          ? buttons.length - 1
          : index - 1
        : index < 0 || index === buttons.length - 1
          ? 0
          : index + 1;
      event.preventDefault();
      buttons[next].focus();
    }
  }

  #isConfirmType(): boolean {
    return this.dialogType === InfoDialogType.CONFIRM || this.dialogType === InfoDialogType.CONFIRM_YESNO;
  }

  /** back to the default buttons for the next question, focus back where it was */
  #afterClose(): void {
    this.confirmLabel = null;
    this.cancelLabel = null;
    this.danger = false;
    this.stayLabel = null;
    const previous = this.#previousFocus;
    this.#previousFocus = null;
    if (previous?.isConnected) {
      setTimeout(() => previous.focus());
    }
  }
}
